<script setup>
import { defineProps, ref, onMounted, nextTick, watch, onBeforeUnmount, defineEmits } from "vue";
import ChevronUp from "@/components/Icons/ChevronUp.vue";
import { t } from "@/controllerLK/AvalLocalization";

const props = defineProps({
  list: {
    type: Array,
    required: true,
    default: () => []
  },
  label: {
    type: String,
    required: true,
    default: () => ""
  },
  titleIcon: {
    type: Object,
    required: false,
    default: () => null
  },
  tagIcon: {
    type: Object,
    required: false,
    default: () => null
  },
  useTrigger: {
    type: Boolean,
    default: false
  },
  useNotDefined: {
    type: Boolean,
    default: true
  },
  notDefinedText: {
    type: String,
    default: null
  },
  accordionItemType: {
    type: String,
    default: "string"
  },
  initiallyExpanded: {
    type: Boolean,
    default: false
  },
});

const emit = defineEmits(["trigger"]);

const isExpanded = ref(false);
const hiddenCount = ref(0);
const containerRef = ref(null);
const listRef = ref(null);
const shouldShowButton = ref(false);

// Функция для расчета переполнения
const calculateOverflow = async () => {
  if (!containerRef.value || !listRef.value || props.list.length === 0) {
    shouldShowButton.value = false;
    hiddenCount.value = 0;
    return;
  }


  const listWrapper = containerRef.value.querySelector(".accordion__list-wrapper");
  const list = listRef.value;

  // Сохраняем текущее состояние
  const previousState = isExpanded.value;

  // Временно разворачиваем для измерения
  isExpanded.value = true;
  await nextTick();

  // Измеряем реальную высоту списка
  const listHeight = list.scrollHeight;
  const singleRowHeight = 24; // примерная высота одного ряда + отступы

  // Проверяем, есть ли переполнение
  const hasOverflow = listHeight > singleRowHeight + 10; // небольшой запас

  if (hasOverflow) {
    shouldShowButton.value = true;

    // Подсчитываем скрытые элементы
    const items = Array.from(list.children);
    let visibleCount = 0;
    let totalWidth = 0;
    const wrapperWidth = listWrapper.offsetWidth - 100; // резерв для кнопки
    const gap = 8;

    for (let i = 0; i < items.length; i++) {
      const itemWidth = items[i].offsetWidth;
      const gapWidth = i > 0 ? gap : 0;

      if (totalWidth + gapWidth + itemWidth <= wrapperWidth) {
        totalWidth += gapWidth + itemWidth;
        visibleCount++;
      } else {
        break;
      }
    }

    hiddenCount.value = Math.max(0, props.list.length - visibleCount);
  } else {
    shouldShowButton.value = false;
    hiddenCount.value = 0;
  }

  // Возвращаем предыдущее состояние
  isExpanded.value = previousState;
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

// Отслеживаем изменения элементов в списке
watch(() => props.list, () => {
  nextTick(() => {
    calculateOverflow();
  });
}, { deep: true, immediate: true });

// Пересчитываем при изменении размера окна
const handleResize = () => {
  calculateOverflow();
};

onMounted(() => {
  nextTick(() => {
    isExpanded.value = props.initiallyExpanded;
    calculateOverflow();
  });
  window.addEventListener("resize", handleResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize);
});

const handleTrigger = () => {
  if (props.useTrigger) {
    emit("trigger");
  }
};
</script>

<template>
  <div
    ref="containerRef"
    class="accordion"
  >
    <span
      class="accordion__title"
      :class="{'accordion__title--trigger': useTrigger}"
      @click="handleTrigger"
    >
      {{ label }}
      <Component
        :is="titleIcon"
        v-if="titleIcon && useTrigger"
        class="accordion__title-icon"
      />
    </span>
    <div class="accordion__content">
      <div
        class="accordion__list-wrapper"
        :class="{ 'accordion__list-wrapper--collapsed': !isExpanded && shouldShowButton }"
      >
        <ul
          ref="listRef"
          class="accordion__list"
          :class="{ 'accordion__list--expanded': isExpanded }"
        >
          <li
            v-if="useNotDefined && list.length === 0"
            class="accordion__list--not-defined"
          >
            {{ notDefinedText ? notDefinedText : t('news_page_body_post.label.not_defined') }}
          </li>

          <!--     Если объект, то выводим поле name + tooltip     -->
          <template v-if="accordionItemType === 'object'">
            <li
              v-for="(item, index) in list"
              :key="item.id || index"
              v-tooltip="item.description"
              class="accordion__item"
            >
              <AvalChip
                :use-hover="false"
                :use-cross-icon="false"
                size="small"
              >
                {{ item.name }}
              </AvalChip>
            </li>
          </template>

          <!--     Если строка выводим как есть.     -->
          <template v-else-if="accordionItemType === 'string'">
            <li
              v-for="(item, index) in list"
              :key="item.id || index"
              class="accordion__item"
            >
              <AvalChip
                :use-hover="false"
                :use-cross-icon="false"
                size="small"
              >
                <Component
                  :is="tagIcon"
                  v-if="tagIcon"
                />
                {{ item }}
              </AvalChip>
            </li>
          </template>

          <template v-else-if="accordionItemType === 'url'">
            <li
              v-for="(item, index) in list"
              :key="item.id || index"
              class="accordion__item-url"
            >
              <a
                target="_blank"
                :href="item"
              >{{ item }}</a>
            </li>
          </template>
          <template v-else-if="accordionItemType === 'default'">
            <li
              v-for="(item, index) in list"
              :key="item.id || index"
              class="accordion__item-default"
            >
              {{ item }}
            </li>
          </template>
        </ul>

        <!-- Градиент для размытия переполнения -->
        <div
          v-if="shouldShowButton && !isExpanded"
          class="accordion__fade-overlay"
        />

        <!-- Кнопка раскрытия списка -->
        <button
          v-if="shouldShowButton && !isExpanded"
          class="accordion__button-expand"
          type="button"
          @click="toggleExpanded"
        >
          +{{ hiddenCount }}
        </button>
      </div>

      <!-- Кнопка сворачивания списка -->
      <button
        v-if="isExpanded && shouldShowButton"
        class="accordion__button-collapse"
        type="button"
        @click="toggleExpanded"
      >
        <ChevronUp />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
.accordion {
  display: flex;
  gap: 10px;

  &__title {
    flex-shrink: 0;
    white-space: nowrap;
    color: var(--text-text-pur);
    height: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 500;

    &-icon {
      width: 14px;
      height: 14px;
    }

    &--trigger {
      cursor: pointer;

      &:hover {
        color: #a7a7a7;
      }
    }
  }

  &__content {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex: 1;
    min-width: 0;
    position: relative;
  }

  &__list-wrapper {
    position: relative;
    flex: 1;
    min-width: 0;

    &--collapsed {
      height: 26px; // высота одной строки
      overflow: hidden;
    }
  }

  &__list {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    flex: 1;
    min-width: 0;
    padding: 1px 0;

    &--not-defined {
      display: flex;
      align-items: center;
      height: 22px;
      color: rgba(123, 135, 165, 0.3);
    }

    &--expanded {
      flex-wrap: wrap;
    }
  }

  &__item {
    flex-shrink: 0;
    white-space: nowrap;
    max-width: 100%;

    &:last-child {
      padding-right: 40px;
    }

    :deep(*) {
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-url {
      width: 100%;
      a {
        overflow: hidden;
        display: inline-block;
        text-overflow: ellipsis;
        white-space: nowrap;
        width: 100%;
        font-size: 12px;
        font-weight: 400;
        color: var(--icon-icon-on);
        text-decoration: none;
        line-height: 22px;
      }
    }

    &-default {
      overflow: hidden;
      display: inline-block;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
      font-size: 12px;
      font-weight: 400;
      color: var(--icon-icon-on);
      text-decoration: none;
      line-height: 22px;
    }
  }

  &__fade-overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 120px;
    background: linear-gradient(to left,
      var(--background-bg-white) 0%,
      var(--background-bg-white) 40%,
      transparent 100%);
    pointer-events: none;
    z-index: 2;
  }

  &__button-expand {
    position: absolute;
    top: 1px;
    right: 0;
    z-index: 3;
    border: 1px solid #7B87A51A;
    padding: 4px 10px;
    background: var(--background-bg-white);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    color: #6B7280;
    height: 24px;
    display: flex;
    align-items: center;

    &:hover {
      background: #F2F3F6;
    }
  }

  &__button-collapse {
    position: absolute;
    right: 0;
    bottom: 0;
    border: 1px solid #7B87A51A;
    padding: 4px 10px;
    background: var(--background-bg-white);
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    user-select: none;
    transition: background-color 0.2s ease;
    color: #6B7280;
    height: 24px;
    display: flex;
    align-items: center;

    &:hover {
      background: #F2F3F6;
    }
  }
}
</style>